import { Sample, AnalysisCatalogItem } from '../types';

/**
 * LIMS Interoperability Module
 * Supports FHIR R4 (JSON) and HL7 v2.5.1 (Pipe-delimited)
 */

interface FHIRResource {
  resourceType: string;
  id: string;
  [key: string]: any;
}

// Robust UUID generator that works in all environments
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// --- FHIR R4 GENERATOR ---

export const generateFHIRBundle = (sample: Sample, catalog: AnalysisCatalogItem[]): any => {
  if (!sample.patients) throw new Error("Patient data missing for FHIR generation");

  const bundleId = generateUUID();
  const timestamp = new Date().toISOString();

  // 1. Patient Resource
  const patientResource: FHIRResource = {
    resourceType: "Patient",
    id: sample.patients.id,
    identifier: [
      {
        use: "official",
        system: "http://lab.ipm.mg/identifiers/patient-id",
        value: sample.patients.unique_id
      }
    ],
    name: [
      {
        family: sample.patients.last_name,
        given: [sample.patients.first_name]
      }
    ],
    gender: sample.patients.gender === 'M' ? 'male' : sample.patients.gender === 'F' ? 'female' : 'other',
    birthDate: sample.patients.birth_date
  };

  // 2. Specimen Resource (The Sample)
  const specimenResource: FHIRResource = {
    resourceType: "Specimen",
    id: sample.id,
    status: "available",
    type: {
      coding: [
        {
          system: "http://snomed.info/sct",
          code: mapSampleTypeToSNOMED(sample.sample_type),
          display: sample.sample_type
        }
      ]
    },
    subject: {
      reference: `Patient/${patientResource.id}`
    },
    collection: {
      collectedDateTime: sample.collection_date
    }
  };

  // 3. ServiceRequest (The Order)
  const serviceRequestResource: FHIRResource = {
    resourceType: "ServiceRequest",
    id: `req-${sample.id}`,
    status: "active",
    intent: "order",
    subject: {
      reference: `Patient/${patientResource.id}`
    },
    specimen: [
      {
        reference: `Specimen/${specimenResource.id}`
      }
    ]
  };

  // 4. Observations (The Results)
  const observations: FHIRResource[] = (sample.results || []).map((res, index) => {
    // Find catalog info for LOINC codes
    const catalogItem = catalog.find(c => c.name === res.analysis);
    const loinc = catalogItem?.loinc_code || "00000-0";
    // const unit = catalogItem?.unit || "";

    return {
      resourceType: "Observation",
      id: `obs-${sample.id}-${index}`,
      status: sample.status === 'validated' ? 'final' : 'preliminary',
      code: {
        coding: [
          {
            system: "http://loinc.org",
            code: loinc,
            display: res.analysis
          }
        ],
        text: res.analysis
      },
      subject: {
        reference: `Patient/${patientResource.id}`
      },
      specimen: {
        reference: `Specimen/${specimenResource.id}`
      },
      effectiveDateTime: res.date,
      valueString: res.result 
    };
  });

  // Construct the Bundle
  return {
    resourceType: "Bundle",
    id: bundleId,
    type: "document",
    timestamp: timestamp,
    entry: [
      { resource: patientResource },
      { resource: specimenResource },
      { resource: serviceRequestResource },
      ...observations.map(obs => ({ resource: obs }))
    ]
  };
};

// --- HL7 v2.5.1 GENERATOR ---

export const generateHL7Message = (sample: Sample, catalog: AnalysisCatalogItem[]): string => {
    if (!sample.patients) return "ERROR: No Patient Data";

    const formatHL7Date = (isoString: string) => {
        try {
            return new Date(isoString).toISOString().replace(/[-:T\.]/g, '').slice(0, 14);
        } catch (e) {
            return new Date().toISOString().replace(/[-:T\.]/g, '').slice(0, 14);
        }
    }

    const timestamp = formatHL7Date(new Date().toISOString());
    const msgControlId = Math.floor(Math.random() * 100000);

    // MSH Segment (Message Header)
    const msh = `MSH|^~\\&|SIL_MYCO|IPM|EHR|HOSP|${timestamp}||ORU^R01|${msgControlId}|P|2.5.1`;

    // PID Segment (Patient Identification)
    const pid = `PID|1||${sample.patients.unique_id}^^^IPM^MR||${sample.patients.last_name}^${sample.patients.first_name}||${sample.patients.birth_date ? sample.patients.birth_date.replace(/-/g, '') : ''}|${sample.patients.gender}`;

    // OBR Segment (Observation Request)
    const obr = `OBR|1|${sample.id}|${sample.id}|80000^Myco Panel^L|||${formatHL7Date(sample.collection_date)}`;

    // OBX Segments (Observation Results)
    const obxSegments = (sample.results || []).map((res, index) => {
        const catalogItem = catalog.find(c => c.name === res.analysis);
        const loinc = catalogItem?.loinc_code || "00000-0";
        const unit = catalogItem?.unit || "";
        
        // OBX|SetID|ValueType|ObservationIdentifier|ObservationSubID|ObservationValue|Units|...
        return `OBX|${index + 1}|ST|${loinc}^${res.analysis}^LN||${res.result}|${unit}||||${sample.status === 'validated' ? 'F' : 'P'}`;
    });

    return [msh, pid, obr, ...obxSegments].join('\r');
};

// Helpers
const mapSampleTypeToSNOMED = (type: string): string => {
  const map: Record<string, string> = {
    'Sang': '119297000',
    'Urine': '122575003',
    'Ecouvillon': '258500001',
    'Selles': '119339001'
  };
  return map[type] || '123038009'; // Default Specimen
};